import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StyledModal from "../../../Modals/StyledModal/StyledModal";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import TertiaryButton from "../../../Buttons/TertiaryButton/TertiaryButton";
import PrimaryTextField from "../../../Inputs/PrimaryTextField/PrimaryTextField";
import CarrierProfileAddEquipmentItem from "./CarrierProfileAddEquipmentItem/CarrierProfileAddEquipmentItem";
import { CarrierService } from "../../../../services/CarrierService";
import { AlertContext } from "../../../Dashboard/Dashboard";

export default function CarrierProfileAddEquipmentModal({ existingTypes, activeCarrier, refreshEquipment }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [open, setOpen] = useState(false);
  const [allEquipmentTypes, setAllEquipmentTypes] = useState([]);
  const [equipmentToDisplay, setEquipmentToDisplay] = useState([]);
  const [equipmentTypesToAdd, setEquipmentTypesToAdd] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleReset = () => {
    setEquipmentTypesToAdd([]);
    handleClose();
  };

  const handleAddType = (type) => {
    const equipmentTypes = [...equipmentTypesToAdd];
    equipmentTypes.push(type);
    setEquipmentTypesToAdd(equipmentTypes);
  };

  const handleRemoveType = (type) => {
    const equipmentTypes = [...equipmentTypesToAdd];
    const index = equipmentTypes.findIndex(equipmentType => equipmentType.id === type.id);
    equipmentTypes.splice(index, 1);
    setEquipmentTypesToAdd(equipmentTypes);
  };
  const handleSave = () => {
    const requiredRequests = equipmentTypesToAdd.length;
    let completedRequests = 0;

    const requestComplete = () => {
      completedRequests++;
      if (completedRequests === requiredRequests) {
        setIsUpdating(false);
        refreshEquipment();
        handleReset();
      }
    };

    setIsUpdating(true);

    equipmentTypesToAdd.forEach(typeToAdd => {
      const payload = {
        equipmentCount: null,
        equipmentTypeId: typeToAdd.id,
        payeeId: activeCarrier.id
      };

      CarrierService.CreateCarrierEquipment(instance, payload).then(() => {
        requestComplete();
      }).catch(error => {
        requestComplete();
        console.error(error);
        warning(error);
      });
    });
  };

  useEffect(() => {
    if (open && !allEquipmentTypes.length) {
      setIsLoading(true);

      CarrierService.GetCarrierEquipmentTypes(instance).then(res => {
        setAllEquipmentTypes(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [open]);

  useEffect(() => {
    setEquipmentToDisplay(allEquipmentTypes);
  }, [allEquipmentTypes]);

  useEffect(() => {
    const types = [...allEquipmentTypes];
    const filteredTypes = types.filter(type => type.descr.toLowerCase().includes(searchInput.toLowerCase()));

    setEquipmentToDisplay(filteredTypes);
  }, [searchInput]);

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "fit-content",
          marginTop: "20px",
          "&:hover": { cursor: "pointer" }
        }}
      >
        <AddIcon sx={{ color: "primary.60", fontSize: "20px" }} />
        <Typography variant="subhead1" color="primary.60">
          Equipment Type
        </Typography>
      </Box>
      <StyledModal
        open={open}
        handleClose={handleClose}
        label="Equipment Type"
        title="Equipment Type"
      >
        <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "80vw", height: "60vh", minWidth: "300px", overflowY: "hidden" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <CircularProgress sx={{ color: "primary.60" }} size={40} />
            </Box>
          ) : (
            <>
              <PrimaryTextField
                id="carrier-profile-add-equipment-modal-search"
                value={searchInput}
                onChange={setSearchInput}
                isSearch
                placeholder="Search"
                width="100%"
              />
              <Box sx={{ overflowY: "auto", marginTop: "4px" }}>
                {equipmentToDisplay.map((equipment) => (
                  <CarrierProfileAddEquipmentItem
                    key={`equipment-item-${equipment.id}`}
                    equipment={equipment}
                    existingTypes={existingTypes}
                    handleAddType={handleAddType}
                    handleRemoveType={handleRemoveType}
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", marginTop: "auto" }}>
                <Box sx={{ margin: "0 12px 0 auto" }}>
                  <TertiaryButton
                    id="carrier-profile-add-equipment-modal-cancel-button"
                    label="Cancel"
                    onClick={handleReset}
                  />
                </Box>
                <Box>
                  <PrimaryButton
                    id="carrier-profile-add-equipment-modal-save-button"
                    label="Add"
                    onClick={handleSave}
                    isLoading={isUpdating}
                    disabled={isUpdating || !equipmentTypesToAdd.length}
                    width="38px"
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </StyledModal>
    </>
  );
}

CarrierProfileAddEquipmentModal.propTypes = {
  existingTypes: PropTypes.array,
  activeCarrier: PropTypes.object.isRequired,
  refreshEquipment: PropTypes.func.isRequired
};